import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  Footer,
  Header,
  NoDataAvailable,
  RelatednessLabel,
  Subtitle,
  Title,
  UnrelatednessLabel,
  Wrapper,
} from './TrendingStyledComponents';
import { TrendingBadge } from './TrendingBadge';
import { SeeEpisodesButton } from './SeeEpisodesButton';
import { useOverviewData } from '@app/hooks/useOverviewData';
import { Loader } from '@components/Loader';
import { useOverviewContext } from '@insights_module/context/OverviewContext';
import { objectToToken } from '@utils/commonUtils';
import { AQLQuery, AQLQueryValue } from '@insights_module/adc_ql/types';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLCompositeQuery,
  AQLPublishedDateQuery,
} from '@insights_module/adc_ql/queries';
import { GARM_CATEGORIES_ARRAY } from '@insights_module/constants/garm';

export const GarmAnalysisChart = () => {
  const { t } = useTranslation();
  const {
    totalRelatedGarmEpisodes,
    isLoadingBrandSafety,
    episodesCount,
    trendingEpisodesCount,
    totalRelatedTrendingGarmEpisodes,
  } = useOverviewData();
  const { selectedDateRange, selectedTrendingDateRange, aqlQueries } = useOverviewContext();

  const relatedPercentage = (
    totalRelatedGarmEpisodes ? (totalRelatedGarmEpisodes / episodesCount) * 100 : 0
  ).toFixed(2);
  const unrelatedPercentage = (
    totalRelatedGarmEpisodes
      ? ((episodesCount - totalRelatedGarmEpisodes) / episodesCount) * 100
      : 100
  ).toFixed(2);

  const trendingRelatedPercentage = (
    totalRelatedTrendingGarmEpisodes
      ? (totalRelatedTrendingGarmEpisodes / trendingEpisodesCount) * 100
      : 0
  ).toFixed(2);
  const trendingUnrelatedPercentage = (
    totalRelatedTrendingGarmEpisodes
      ? ((trendingEpisodesCount - totalRelatedTrendingGarmEpisodes) / trendingEpisodesCount) * 100
      : 100
  ).toFixed(2);

  const handleSeeRelatedEpisodeList = () => {
    const url = `/insights/episodes?query_token=${objectToToken(aqlQueries.brandSafetyGarmRiskOverviewQuery)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSeeUnrelatedEpisodeList = () => {
    const query = { all: [] as AQLQueryValue[] };

    if (selectedTrendingDateRange.description !== 'all_time') {
      query.all.push(
        new AQLCompositeQuery('none', [
          ...GARM_CATEGORIES_ARRAY.map(
            (category) =>
              new AQLBrandSafetyGarmQuery([
                new AQLBrandSafetyGarmFilterQuery(category, 'range', { gte: 'high', lte: 'floor' }),
              ]) as AQLQuery
          ),
        ])
      );

      query.all.push(
        new AQLBrandSafetyGarmQuery([
          new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
            gte: 'unrelated',
            lte: 'floor',
          }),
        ])
      );
    }

    if (selectedDateRange.minDate && selectedDateRange.maxDate) {
      query.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate,
          lte: selectedDateRange.maxDate,
        })
      );
    }

    const url = `/insights/episodes?query_token=${objectToToken(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Wrapper
      tw='flex-none justify-start w-full sm:w-[353px] items-center'
      css={[!isLoadingBrandSafety && !episodesCount && tw`h-auto items-start`]}>
      <Title>{t('insights.overview_page.brand_safety_section.garm_analysis')}</Title>
      {isLoadingBrandSafety ? (
        <Loader size='md' theme='dark' />
      ) : !episodesCount ? (
        <NoDataAvailable>{t('insights.overview_page.no_data')}</NoDataAvailable>
      ) : (
        <>
          <Wrapper tw='w-full h-auto gap-[8px]'>
            <Header>
              <Title>{`${t('insights.overview_page.brand_safety_section.garm_analysis_chart_levels.high')} + ${t('insights.overview_page.brand_safety_section.garm_analysis_chart_levels.floor')} `}</Title>
              <SeeEpisodesButton onClick={handleSeeRelatedEpisodeList} />
            </Header>

            <RelatednessLabel>{relatedPercentage} %</RelatednessLabel>

            <Footer tw='justify-between'>
              <Title>{totalRelatedGarmEpisodes} ep</Title>
              {selectedDateRange.description !== 'all_time' && (
                <div tw='flex gap-[4px] items-center'>
                  <TrendingBadge
                    value={Number(relatedPercentage) - Number(trendingRelatedPercentage)}
                  />
                  <Subtitle tw='text-center'>
                    {t('insights.overview_page.brand_safety_section.vs_previous', {
                      days: selectedTrendingDateRange.days,
                    })}
                  </Subtitle>
                </div>
              )}
            </Footer>
          </Wrapper>

          <Wrapper tw='w-full h-auto gap-[8px]'>
            <Header tw='items-start'>
              <div tw='flex flex-col justify-start'>
                <Title>
                  {t('insights.overview_page.brand_safety_section.other_categories_title')}
                </Title>
                <Subtitle>
                  {t('insights.overview_page.brand_safety_section.garm_other_categories_subtitle')}
                </Subtitle>
              </div>
              <SeeEpisodesButton onClick={handleSeeUnrelatedEpisodeList} />
            </Header>

            <UnrelatednessLabel>{unrelatedPercentage} %</UnrelatednessLabel>

            <Footer tw='justify-between items-center'>
              <Title>{episodesCount - totalRelatedGarmEpisodes} ep</Title>
              {selectedDateRange.description !== 'all_time' && (
                <div tw='flex gap-[4px] items-center'>
                  <TrendingBadge
                    value={Number(unrelatedPercentage) - Number(trendingUnrelatedPercentage)}
                  />
                  <Subtitle tw='text-center'>
                    {t('insights.overview_page.brand_safety_section.vs_previous', {
                      days: selectedTrendingDateRange.days,
                    })}
                  </Subtitle>
                </div>
              )}
            </Footer>
          </Wrapper>
        </>
      )}
    </Wrapper>
  );
};
